import { useAuth } from 'providers/auth-provider'
import { salesUsersCanUpdate } from '../../helpers/sales-users-access-helper'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useParams } from 'react-router'
import {
  Button,
  Card,
  CardBody,
  Col,
  Collapse,
  Container,
  Label,
  ListGroup,
  ListGroupItem,
  Row,
  Table,
} from 'reactstrap'

import SettlementProfile from '../../components/settlement-profile'
import SalesUsersApi from 'api/admins/sales-users-api'
import { TitlePage, RedirectButton } from 'components/atoms'
import { ALERT_TYPES } from 'constants/alert-constants'
import SalesUsersForm from 'domains/sales/users/forms/user-form/sales-users-form'

const LIST_PATH = '/app/sales/users'

const getPrimaryDetail = (user) => {
  const details = Array.isArray(user?.details) ? user.details : []
  return details.find((d) => !!d?.primary && String(d?._destroy || '0') !== '1') || details[0] || null
}

const getKycActionFlags = (detail) => {
  const state = detail?.state || ''

  return {
    state,
    canApprove: ['waiting_for_verification', 'created', 'suspended'].includes(state),
    canReject: ['waiting_for_verification', 'created'].includes(state),
    canSuspend: state === 'verified',
  }
}

const stringifyHistoryValue = (value) => {
  if (value === null || value === undefined || value === '') return '-'
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2)
    } catch (_) {
      return String(value)
    }
  }
  return String(value)
}

const buildHistoryTableData = (historyDetail) => {
  const before = historyDetail?.before || {}
  const after = historyDetail?.after || {}

  const keys = Array.from(new Set([
    ...Object.keys(before || {}),
    ...Object.keys(after || {}),
  ])).sort()

  return keys.map((key) => {
    const beforeRaw = before?.[key]
    const afterRaw = after?.[key]

    const beforeVal = stringifyHistoryValue(beforeRaw)
    const afterVal = stringifyHistoryValue(afterRaw)

    return {
      fieldName: key,
      before: beforeVal,
      after: afterVal,
      sameValue: beforeVal === afterVal,
    }
  })
}

const SalesUsersDetailPage = ({ pageUtils }) => {
  const { user: currentAdmin } = useAuth()
  const canUpdateUser = salesUsersCanUpdate(currentAdmin)

  const { id } = useParams()
  const userId = id

  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [kycSubmitting, setKycSubmitting] = useState(false)
  const saveActionRef = useRef(null)

  const [user, setUser] = useState(null)

  const [isHistoryOpen, setIsHistoryOpen] = useState(false)
  const [showDifferenceOnly, setShowDifferenceOnly] = useState(false)
  const [historyList, setHistoryList] = useState([])
  const [historyDetail, setHistoryDetail] = useState({})
  const [historyLoading, setHistoryLoading] = useState(false)

  const fetchUser = useCallback(async () => {
    setLoading(true)
    try {
      const response = await SalesUsersApi.show({ userId })
      setUser(response?.data?.user || null)
    } catch (e) {
      pageUtils?.setApiErrorMsg?.(e?.response?.data)
      pageUtils?.setAlertMsg?.('Gagal memuat user detail.', ALERT_TYPES.ERROR)
    } finally {
      setLoading(false)
    }
  }, [pageUtils, userId])

  const fetchHistoryList = useCallback(async () => {
    setHistoryLoading(true)
    try {
      const response = await SalesUsersApi.listHistory({ userId })
      const rows = Array.isArray(response?.data?.data) ? response.data.data : []
      setHistoryList(rows.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)))
    } catch (e) {
      pageUtils?.setApiErrorMsg?.(e?.response?.data)
      pageUtils?.setAlertMsg?.('Gagal memuat user history list.', ALERT_TYPES.ERROR)
    } finally {
      setHistoryLoading(false)
    }
  }, [pageUtils, userId])

  const fetchHistoryDetail = useCallback(async (historyId) => {
    try {
      const response = await SalesUsersApi.getHistory({ userId, historyId })
      setHistoryDetail(response?.data?.data || {})
    } catch (e) {
      pageUtils?.setApiErrorMsg?.(e?.response?.data)
      pageUtils?.setAlertMsg?.('Gagal memuat user history detail.', ALERT_TYPES.ERROR)
    }
  }, [pageUtils, userId])

  const clearHistoryDetail = useCallback(() => {
    setHistoryDetail({})
  }, [])

  useEffect(() => {
    fetchUser()
  }, [fetchUser])

  useEffect(() => {
    if (isHistoryOpen) fetchHistoryList()
  }, [isHistoryOpen, fetchHistoryList])

  const onSubmit = async (payload) => {
    if (!canUpdateUser) return null
    setSubmitting(true)
    try {
      const response = await SalesUsersApi.update({ userId, payload })
      const updated = response?.data?.user

      pageUtils?.setAlertMsg?.('User berhasil disimpan.', ALERT_TYPES.SUCCESS)

      if (updated) setUser(updated)
      else await fetchUser()

      if (isHistoryOpen) await fetchHistoryList()

      return updated
    } catch (e) {
      pageUtils?.setApiErrorMsg?.(e?.response?.data)
      pageUtils?.setAlertMsg?.(e?.response?.data?.message || e?.message || 'Update user gagal.', ALERT_TYPES.ERROR)
      throw e
    } finally {
      setSubmitting(false)
    }
  }

  const onKycUpdate = async (state) => {
    if (!canUpdateUser) return null
    const stateLabelMap = {
      approve_kyc: 'approve KYC',
      reject_kyc: 'reject KYC',
      suspend_user: 'suspend user',
    }

    const label = stateLabelMap[state] || 'update KYC'
    const ok = window.confirm(`Yakin ingin ${label} untuk user #${userId}?`)
    if (!ok) return

    setKycSubmitting(true)
    try {
      const response = await SalesUsersApi.kycUpdate({ userId, state })
      const updated = response?.data?.user

      pageUtils?.setAlertMsg?.(
        response?.data?.message || 'Successfully update user KYC status.',
        ALERT_TYPES.SUCCESS
      )

      if (updated) setUser(updated)
      else await fetchUser()

      if (isHistoryOpen) await fetchHistoryList()
    } catch (e) {
      pageUtils?.setApiErrorMsg?.(e?.response?.data)
      pageUtils?.setAlertMsg?.(
        e?.response?.data?.message || 'Gagal update KYC status.',
        ALERT_TYPES.ERROR
      )
    } finally {
      setKycSubmitting(false)
    }
  }

  const mainDetail = useMemo(() => getPrimaryDetail(user), [user])
  const { state: kycState, canApprove, canReject, canSuspend } = useMemo(
    () => getKycActionFlags(mainDetail),
    [mainDetail]
  )

  const tableData = useMemo(() => buildHistoryTableData(historyDetail), [historyDetail])

  return (
    <>
      <TitlePage mainTitle={`User Detail #${userId}`} />

      <Container fluid>
        <Row>
          <Col lg="12" className="d-flex justify-content-between align-items-center mb-3">
            <RedirectButton path={LIST_PATH} text="Back to List" color="secondary" />
            <Button color="primary" disabled={!canUpdateUser || submitting || kycSubmitting || loading} onClick={() => saveActionRef.current?.()}>
              {submitting ? 'Saving...' : 'Save'}
            </Button>
          </Col>

          <Col lg="12">
            <Card>
              <CardBody>
                {loading ? (
                  <div>Loading...</div>
                ) : (
                  <>
                    <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
                      <div>
                        <h5 className="mb-1">KYC Actions</h5>
                        <div className="text-muted">
                          Current primary detail state: <b>{kycState || '-'}</b>
                        </div>
                        {mainDetail?.id ? (
                          <div className="text-muted">
                            Primary Detail ID: <b>{mainDetail.id}</b>
                          </div>
                        ) : null}
                      </div>

                      <div className="mt-2 mt-md-0">
                        {canApprove ? (
                          <Button
                            color="success"
                            className="mr-2"
                            disabled={!canUpdateUser || kycSubmitting || submitting}
                            onClick={() => onKycUpdate('approve_kyc')}
                          >
                            {kycState === 'suspended' ? 'Unsuspend User' : 'Approve KYC'}
                          </Button>
                        ) : null}

                        {canReject ? (
                          <Button
                            color="danger"
                            className="mr-2"
                            disabled={!canUpdateUser || kycSubmitting || submitting}
                            onClick={() => onKycUpdate('reject_kyc')}
                          >
                            Reject KYC
                          </Button>
                        ) : null}

                        {canSuspend ? (
                          <Button
                            color="warning"
                            disabled={!canUpdateUser || kycSubmitting || submitting}
                            onClick={() => onKycUpdate('suspend_user')}
                          >
                            Suspend User
                          </Button>
                        ) : null}
                      </div>
                    </div>

                    <hr />

                    <SettlementProfile.Provider userId={ userId }>
                    <SettlementProfile userId={ userId } currentAdmin={ currentAdmin } onSaved={ fetchUser } />

                    <fieldset
                      data-sales-users-read-only-guard
                      disabled={!canUpdateUser}
                      aria-disabled={!canUpdateUser}
                      style={{ border: 0, margin: 0, minWidth: 0, padding: 0 }}
                    >
                      <div style={!canUpdateUser ? { pointerEvents: 'none' } : undefined}>
                        <SalesUsersForm
                          saveActionRef={saveActionRef}
                          renderBankAccounts={ () => <SettlementProfile userId={ userId } currentAdmin={ currentAdmin } onSaved={ fetchUser } section="accounts" /> }
                          renderBankVerification={ (detail, dirty) => detail.id && <SettlementProfile
                            userId={ userId } currentAdmin={ currentAdmin } onSaved={ fetchUser } section='bank' detailId={ detail.id }
                            disabled={ dirty }
                          /> }
                          mode="edit"
                          user={user}
                          submitting={submitting || kycSubmitting}
                          onSubmit={onSubmit}
                        />
                      </div>
                    </fieldset>
                    </SettlementProfile.Provider>

                    <hr />

                    <div className="d-flex flex-wrap align-items-center mb-3">
                      <Button
                        color="info"
                        onClick={() => {
                          setIsHistoryOpen(!isHistoryOpen)
                          if (isHistoryOpen) {
                            clearHistoryDetail()
                            setShowDifferenceOnly(false)
                          }
                        }}
                      >
                        {isHistoryOpen ? 'Hide History' : 'Lihat History'}
                      </Button>
                    </div>

                    <Collapse isOpen={isHistoryOpen}>
                      <Card>
                        <CardBody>
                          <h4 className="mb-3 text-center">User History</h4>

                          {historyLoading ? (
                            <div>Loading history...</div>
                          ) : (
                            <ListGroup>
                              {historyList.map((history) => {
                                const isActive = history.id === historyDetail.id

                                return (
                                  <React.Fragment key={history.id}>
                                    <ListGroupItem
                                      action
                                      active={isActive}
                                      tag="button"
                                      onClick={() => {
                                        setShowDifferenceOnly(false)
                                        if (isActive) {
                                          clearHistoryDetail()
                                        } else {
                                          fetchHistoryDetail(history.id)
                                        }
                                      }}
                                    >
                                      {new Date(history.created_at).toLocaleString()}: ({history.done_by}){' '}
                                      {String(history.action_type || '').replaceAll('_', ' ')}
                                    </ListGroupItem>

                                    {isActive ? (
                                      <>
                                        <Label className="text-muted text-nowrap mt-2">
                                          Lihat field yang berubah saja
                                        </Label>
                                        <Label className="custom-toggle mb-4">
                                          <span></span>
                                          <input
                                            type="checkbox"
                                            value={showDifferenceOnly}
                                            onChange={() => setShowDifferenceOnly(!showDifferenceOnly)}
                                          />
                                          <span
                                            className="custom-toggle-slider rounded-circle text-success"
                                            data-label-off="No"
                                            data-label-on="Yes"
                                          />
                                        </Label>

                                        <Table striped responsive>
                                          <thead>
                                            <tr>
                                              <th>Field</th>
                                              <th>Before</th>
                                              <th>After</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {tableData
                                              .filter((o) => (showDifferenceOnly ? !o.sameValue : true))
                                              .map((o) => (
                                                <tr key={o.fieldName}>
                                                  <th scope="row">{o.fieldName}</th>
                                                  <td style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                    {o.before}
                                                  </td>
                                                  <td style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                    {o.after}
                                                  </td>
                                                </tr>
                                              ))}
                                          </tbody>
                                        </Table>
                                      </>
                                    ) : null}
                                  </React.Fragment>
                                )
                              })}

                              {historyList.length === 0 ? (
                                <div className="text-muted">No history yet.</div>
                              ) : null}
                            </ListGroup>
                          )}
                        </CardBody>
                      </Card>
                    </Collapse>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  )
}

SalesUsersDetailPage.propTypes = {
  pageUtils: PropTypes.object,
}

export default SalesUsersDetailPage
